import React, { useEffect, useState, createContext, useContext } from 'react';
import { HomePage } from './pages/HomePage';
import { ProjectPage } from './pages/ProjectPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { AdminPage } from './pages/AdminPage';
import { LoginPage } from './pages/LoginPage';
import { LoadingScreen } from './components/LoadingScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import { cache, CACHE_KEYS } from './lib/cache';
import type { Project, ProjectsPageItem } from './lib/supabase';
// Theme context
interface ThemeContextType {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}
export const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {}
});
export const useTheme = () => useContext(ThemeContext);
// Load theme from localStorage
const loadTheme = (): 'dark' | 'light' => {
  try {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
  } catch (error) {
    console.error('Error loading theme from localStorage:', error);
  }
  return 'dark';
};
function AppContent() {
  const [currentView, setCurrentView] = useState<'home' | 'project' | 'projects' | 'admin'>('home');
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsPageData, setProjectsPageData] = useState<ProjectsPageItem[]>([]);
  const [theme, setTheme] = useState<'dark' | 'light'>(loadTheme);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const {
    user,
    loading: authLoading
  } = useAuth();
  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('light', theme === 'light');
  }, [theme]);
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };
  // Load projects from Supabase with caching (optimized query)
  const loadProjects = async () => {
    setIsLoadingProjects(true);
    try {
      // Check cache first
      const cached = cache.get<Project[]>(CACHE_KEYS.PROJECTS);
      if (cached) {
        setProjects(cached);
        setIsLoadingProjects(false);
        return;
      }
      // Optimized query - only fetch needed fields
      const {
        data,
        error
      } = await supabase.from('projects').select('id, title, category, year, image, thumbnail, media_type, description, position').order('position', {
        ascending: true
      });
      if (error) throw error;
      if (data) {
        setProjects(data as Project[]);
        // Cache the results
        cache.set(CACHE_KEYS.PROJECTS, data);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };
  // Load projects page data from Supabase with caching (optimized query)
  const loadProjectsPageData = async () => {
    try {
      // Check cache first
      const cached = cache.get<ProjectsPageItem[]>(CACHE_KEYS.PROJECTS_PAGE);
      if (cached) {
        setProjectsPageData(cached);
        return;
      }
      // Optimized query - only fetch needed fields
      const {
        data,
        error
      } = await supabase.from('projects_page').select('id, title, description, image, link, category, button_text, button_disabled, position').order('position', {
        ascending: true
      });
      if (error) throw error;
      if (data) {
        setProjectsPageData(data as ProjectsPageItem[]);
        // Cache the results
        cache.set(CACHE_KEYS.PROJECTS_PAGE, data);
      }
    } catch (error) {
      console.error('Error loading projects page data:', error);
    }
  };
  // Initial data load - non-blocking
  useEffect(() => {
    const loadData = async () => {
      // Load data in background without blocking render
      await Promise.all([loadProjects(), loadProjectsPageData()]);
    };
    loadData();
  }, []);
  const handleProjectClick = (project: any) => {
    setSelectedProject(project);
    setCurrentView('project');
  };
  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedProject(null);
  };
  const handleGoToAdmin = () => {
    setCurrentView('admin');
  };
  const handleGoToProjects = () => {
    setCurrentView('projects');
  };
  const handleBackFromAdmin = () => {
    setCurrentView('home');
  };
  // Don't block rendering - load immediately
  // Auth check happens in background
  // if (authLoading) {
  //   return <LoadingScreen />
  // }
  // Show login page if trying to access admin without auth
  if (currentView === 'admin' && !user) {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-0">
        <LoginPage data-id="element-1" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'admin') {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-2">
        <AdminPage projects={projects} projectsPageData={projectsPageData} onUpdateProjects={async () => {
        // Clear cache when updating
        cache.clear(CACHE_KEYS.PROJECTS);
        await loadProjects();
      }} onUpdateProjectsPageData={async () => {
        // Clear cache when updating
        cache.clear(CACHE_KEYS.PROJECTS_PAGE);
        await loadProjectsPageData();
      }} onBack={handleBackFromAdmin} data-id="element-3" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'projects') {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-4">
        <ProjectsPage projects={projectsPageData} onGoBack={handleBackToHome} onGoToAdmin={handleGoToAdmin} data-id="element-5" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'project' && selectedProject) {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-6">
        <ProjectPage project={selectedProject} onBack={handleBackToHome} data-id="element-7" />
      </ThemeContext.Provider>;
  }
  return <ThemeContext.Provider value={{
    theme,
    toggleTheme
  }} data-id="element-8">
      <HomePage projects={projects} onProjectClick={handleProjectClick} onGoToAdmin={handleGoToAdmin} onGoToProjects={handleGoToProjects} isLoadingProjects={isLoadingProjects} data-id="element-9" />
    </ThemeContext.Provider>;
}
export function App() {
  return <AuthProvider data-id="element-10">
      <AppContent data-id="element-11" />
    </AuthProvider>;
}